import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, SignIn, useAuth } from './app/auth';
import { Shell } from './app/Shell';
import { DashboardPage } from './features/Dashboard';
import { OrdersPage } from './features/Orders';
import { OrderDetailPage } from './features/OrderDetail';
import { ApprovalsPage, AuditPage, FindingsPage } from './features/Pages';
import { ProfilePage } from './features/Profile';
import { AdminPage } from './features/Admin';
import {
  PayrollShell, EmployeesPage, EmployeeDetailPage, PayComponentsPage,
  StructuresPage, PayRunsPage, PayRunDetailPage, StatutoryPage,
} from './features/Payroll';
import { BeneficiariesPage } from './features/Beneficiaries';
import { Skeleton, ToastProvider } from './shared/ui';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        // Never retry an auth or permission failure; it will not succeed.
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return count < 2;
      },
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  },
});

function Gate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ padding: 40, maxWidth: 700, margin: '0 auto' }}>
        <Skeleton rows={4} />
      </div>
    );
  }
  if (!user) return <SignIn />;

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<DashboardPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="findings" element={<FindingsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="beneficiaries" element={<BeneficiariesPage />} />
        <Route path="payroll" element={<PayrollShell />}>
          <Route index element={<Navigate to="employees" replace />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="employees/:id" element={<EmployeeDetailPage />} />
          <Route path="components" element={<PayComponentsPage />} />
          <Route path="structures" element={<StructuresPage />} />
          <Route path="runs" element={<PayRunsPage />} />
          <Route path="runs/:id" element={<PayRunDetailPage />} />
          <Route path="statutory" element={<StatutoryPage />} />
        </Route>
        <Route
          path="admin"
          element={user.role === 'ADMIN' ? <AdminPage /> : <Navigate to="/" replace />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ── PWA service worker ────────────────────────────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Service worker registration failed — app still works, just no offline support.
    });
  });
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Gate />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
